import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6'];

function ChartRenderer({ chart }) {
  if (!chart || !chart.data || !chart.type) return null;
  const { type, title, data, xKey, yKeys: rawYKeys, dataKey, nameKey } = chart;

  // Normalize yKeys: accept strings ["revenue"] or objects [{key:"revenue",label:"Revenue"}]
  const yKeys = (rawYKeys || []).map(k => typeof k === 'string' ? k : k.key);

  const wrap = (children) => (
    <div style={{ marginTop: 8 }}>
      {title && <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', marginBottom: 4 }}>{title}</div>}
      <ResponsiveContainer width="100%" height={240}>{children}</ResponsiveContainer>
    </div>
  );

  if (type === 'bar' && xKey && yKeys.length) {
    return wrap(
      <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {yKeys.map((k, i) => <Bar key={k} dataKey={k} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />)}
      </BarChart>
    );
  }

  if (type === 'line' && xKey && yKeys.length) {
    return wrap(
      <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {yKeys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />)}
      </LineChart>
    );
  }

  if (type === 'area' && xKey && yKeys.length) {
    return wrap(
      <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {yKeys.map((k, i) => <Area key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.15} strokeWidth={2} />)}
      </AreaChart>
    );
  }

  if (type === 'pie' && dataKey && nameKey) {
    return wrap(
      <PieChart>
        <Pie data={data} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    );
  }

  return null;
}

function PmsStaffAssistant({ token, selectedProperty }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  // Load history on mount
  useEffect(() => {
    if (!selectedProperty?.id) return;
    (async () => {
      try {
        const res = await fetch('/api/v1/ai/staff-assistant/history?limit=30', { headers: headers() });
        const data = await res.json();
        if (res.ok && data.messages) {
          setMessages(data.messages.map(m => ([
            { role: 'user', text: m.user_message, time: m.created_at },
            { role: 'ai', text: m.ai_response, intent: m.detected_intent, time: m.created_at },
          ])).flat());
        }
      } catch (_) { /* ignore */ }
    })();
  }, [selectedProperty?.id, headers]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const toggleView = (idx) => {
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, viewMode: m.viewMode === 'text' ? 'chart' : 'text' } : m));
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setInput('');
    setError('');
    setMessages(prev => [...prev, { role: 'user', text: msg, time: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await fetch('/api/v1/ai/staff-assistant/chat', {
        method: 'POST', headers: headers(true), body: JSON.stringify({ message: msg }),
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) throw new Error('Server unavailable — please try again in a moment');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      const hasChart = data.chart && data.chart.data && data.chart.type;
      setMessages(prev => [...prev, {
        role: 'ai', text: data.reply, intent: data.intent, data: data.data,
        chart: data.chart || null, viewMode: hasChart ? 'chart' : 'text',
        time: new Date().toISOString(),
      }]);
    } catch (err) {
      setError(err.message);
      setMessages(prev => [...prev, { role: 'ai', text: `Error: ${err.message}`, time: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const quickChips = ['Occupancy today', 'Revenue this week', 'Open HK tasks', 'Tomorrow departures', 'ADR this month'];

  if (!selectedProperty?.id) {
    return (<div style={{ padding: '1rem', maxWidth: 800 }}><h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Staff Assistant</h2><p style={{ color: 'var(--theme-text-muted)' }}>Please select a property.</p></div>);
  }

  return (
    <div style={{ padding: '1rem', maxWidth: 800, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      <div>
        <h2 style={{ marginTop: 0, color: 'var(--theme-primary)', marginBottom: 4 }}>AI Staff Assistant</h2>
        <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: '0 0 0.5rem 0' }}>Ask anything about your hotel operations</p>
      </div>

      {/* Quick chips */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {quickChips.map(c => (
          <button key={c} onClick={() => sendMessage(c)} disabled={sending}
            style={{ padding: '0.3rem 0.7rem', borderRadius: 16, border: '1px solid var(--theme-border)', background: 'var(--theme-bg-light)', color: '#475569', cursor: 'pointer', fontSize: '0.78rem' }}>
            {c}
          </button>
        ))}
      </div>

      {/* Chat area */}
      <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1rem', background: 'var(--theme-bg-light)', marginBottom: '0.75rem' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--theme-text-muted)', padding: '2rem 0' }}>
            <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>How can I help?</div>
            <div style={{ fontSize: '0.85rem' }}>Ask about occupancy, revenue, guests, housekeeping, or any hotel operations question.</div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '0.6rem' }}>
            <div style={{
              maxWidth: m.chart ? '95%' : '80%', padding: '0.6rem 1rem', borderRadius: 12,
              background: m.role === 'user' ? 'var(--theme-primary)' : '#fff',
              color: m.role === 'user' ? '#fff' : '#1e293b',
              border: m.role === 'ai' ? '1px solid var(--theme-border)' : 'none',
              fontSize: '0.88rem', lineHeight: 1.5,
            }}>
              {/* View toggle for messages with chart data */}
              {m.chart && m.role === 'ai' && (
                <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                  <button onClick={() => toggleView(i)}
                    style={{
                      padding: '2px 10px', borderRadius: 4, border: '1px solid var(--theme-border)',
                      background: m.viewMode === 'chart' ? '#6366f1' : '#fff',
                      color: m.viewMode === 'chart' ? '#fff' : '#475569',
                      cursor: 'pointer', fontSize: '0.7rem', fontWeight: 500,
                    }}>
                    Chart
                  </button>
                  <button onClick={() => toggleView(i)}
                    style={{
                      padding: '2px 10px', borderRadius: 4, border: '1px solid var(--theme-border)',
                      background: m.viewMode === 'text' ? '#6366f1' : '#fff',
                      color: m.viewMode === 'text' ? '#fff' : '#475569',
                      cursor: 'pointer', fontSize: '0.7rem', fontWeight: 500,
                    }}>
                    Text
                  </button>
                </div>
              )}

              {/* Chart view */}
              {m.chart && m.viewMode === 'chart' && (
                <ChartRenderer chart={m.chart} />
              )}

              {/* Text view (always show if no chart, or when viewMode is text) */}
              {(!m.chart || m.viewMode === 'text') && (
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
              )}

              {m.intent && m.role === 'ai' && (
                <div style={{ fontSize: '0.65rem', color: 'var(--theme-text-muted)', marginTop: 4 }}>{m.intent}</div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.6rem' }}>
            <div style={{ padding: '0.6rem 1rem', borderRadius: 12, background: '#fff', border: '1px solid var(--theme-border)', color: 'var(--theme-text-muted)', fontSize: '0.88rem' }}>
              Thinking...
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {error && <div className="error-banner" style={{ marginBottom: '0.5rem', fontSize: '0.82rem' }}>{error}</div>}

      {/* Input */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
          placeholder="Ask about occupancy, revenue, guests..."
          disabled={sending}
          style={{ flex: 1, padding: '0.6rem 1rem', border: '1px solid var(--theme-border)', borderRadius: 8, fontSize: '0.9rem', outline: 'none' }}
        />
        <button onClick={() => sendMessage()} disabled={sending || !input.trim()}
          style={{ padding: '0.6rem 1.2rem', borderRadius: 8, border: 'none', background: sending ? 'var(--theme-text-muted)' : '#6366f1', color: '#fff', fontWeight: 600, cursor: sending ? 'wait' : 'pointer', fontSize: '0.9rem' }}>
          Send
        </button>
      </div>
    </div>
  );
}

export default PmsStaffAssistant;
